
    import { defineComponent } from 'vue';
    import XBreadcrumb from '@/apps/shared/components/breadcrumb.vue';
    import webSaleHelper from '@/apps/shared/utils/webSaleHelper';
    import travelAssistanceService from '@/apps/shared/services/travelAssistance-service';

    const SINGLE_TRIP_MIN_RETURN_OFFSET_DAYS = 2;

    export default defineComponent({
        name: 'travelAssistance-tab-two',
        components: {
            XBreadcrumb,
        },
        data() {
            // La salida se permite recién desde mañana (hoy + 1); medianoche para que el
            // datepicker habilite ese día completo sin depender de la hora actual.
            const departureDateMin = new Date();
            departureDateMin.setDate(departureDateMin.getDate() + 1);
            departureDateMin.setHours(0, 0, 0, 0);

            return {
                breadcrumItems: [
                    { text: 'Inicio', path: '/asistencia-viajero' },
                    { text: 'Datos del Viaje', path: '/asistencia-viajero/datos-viaje' },
                ],
                // Selecciones hechas en las pantallas de cards previas (destino / tipo de viaje)
                selectedDestination: null,
                selectedTripType: null,
                // Evita que el watcher de departureDate recalcule la fecha de regreso
                // mientras se restauran los datos guardados al volver de planes
                isRestoringData: false,
                // Fechas
                departureDate: new Date(),
                departureDateMin: departureDateMin,
                returnDate: new Date(),
                returnDateMin: new Date(),
                returnDateKey: new Date().getTime(),
                serviceDateError: null as string | null,
                dateValidationSeq: 0,
                format: 'DD/MM/YYYY',
                messageErrorDate: null,
                // Pasajeros
                passengersUnder21: 0,
                passengersBetween21And75: 0,
                passengersOver75: 0,
                passengersTotalMax: 9,
                passengerGroups: [
                    { key: 'passengersUnder21', label: 'Menores a 21 años' },
                    { key: 'passengersBetween21And75', label: 'Entre 21 y 75 años' },
                    { key: 'passengersOver75', label: 'Entre 76 y 84 años' },
                ],
                messageErrorPassengers: null,
                // Cuotas (mock)
                allInstallmentOptions: [
                    { id: 1, description: '1 cuota' },
                    { id: 3, description: '3 cuotas' },
                    { id: 6, description: '6 cuotas' },
                    { id: 12, description: '12 cuotas' },
                ],
                selectedInstallment: null,
                // Autocomplete config (misma config usada en otros productos)
                autocompleteConfig: {
                    formatResult: function (item: any) {
                        return item.description;
                    },
                    selection: function (item: any) {
                        return item.description;
                    },
                },
                referente: null,
                quoteLoadingTimer: null as any,
            }
        },
        computed: {
            // El id llega numérico desde el backend; se normaliza para no depender del tipo
            isSingleTrip(): boolean {
                return this.selectedTripType != null && String(this.selectedTripType.id) === '1';
            },

            isReturnDateDisabled(): boolean {
                return this.selectedTripType != null && !this.isSingleTrip;
            },

            returnDateMax(): Date | null {
                if (!this.isSingleTrip || this.departureDate == null) {
                    return null;
                }
                // la duracion real la valida Quotator
                const max = new Date(this.departureDate);
                max.setFullYear(max.getFullYear() + 1);
                return max;
            },
            departureDateMax(): Date {
                const max = new Date();
                max.setFullYear(max.getFullYear() + 1);
                return max;
            },
            totalPassengers(): number {
                return (Number(this.passengersUnder21) || 0)
                    + (Number(this.passengersBetween21And75) || 0)
                    + (Number(this.passengersOver75) || 0);
            },

            installmentOptions(): any[] {
                if (this.isSingleTrip) {
                    return this.allInstallmentOptions.filter((o: any) => o.id === 1);
                }
                return this.allInstallmentOptions;
            },
        },
        methods: {
            async pageCustomization() {
                const defaultTitle = 'Asistencia al Viajero';
                const actualPath = '/asistencia-viajero';
                try {
                    await webSaleHelper.pageCustomization(this.$route, this.Bus, this.$store, defaultTitle, actualPath, null, null);
                }
                catch (error) {
                    const statusCode = error.response?.status;
                    this.Bus.$emit(
                        'show-service-fail',
                        statusCode,
                        [() => this.pageCustomization()]);
                }
            },

            updateByProductGoogleAnalytics() {
                window.globalThis.dataLayer = window.globalThis.dataLayer || [];
                const productGtmName = 'DefaultTravelAssistanceGTMInfo';
                this.Bus.$emit('updateByProductGoogleAnalytics', productGtmName);
            },

            addPassenger(category: string) {
                if (this.totalPassengers >= this.passengersTotalMax) return;
                this.$data[category] = (Number(this.$data[category]) || 0) + 1;
            },

            restPassenger(category: string) {
                const current = Number(this.$data[category]) || 0;
                if (current > 0) this.$data[category] = current - 1;
            },

            clampPassengerGroup(category: string, newVal: any) {
                let value = Number(newVal);
                if (isNaN(value) || value < 0) value = 0;
                value = Math.floor(value);
                const others = this.totalPassengers - (Number(this.$data[category]) || 0);
                const maxAllowed = this.passengersTotalMax - others;
                if (value > maxAllowed) value = maxAllowed;
                if (this.$data[category] !== value) this.$data[category] = value;
            },

            async cotizar() {
                this.messageErrorDate = null;
                this.messageErrorPassengers = null;
                var validDepartureDate = this.departureDate != null;
                var validReturnDate = this.returnDate != null;
                var validInstallment = this.selectedInstallment != null;

                // La salida debe ser desde mañana (el datepicker lo limita, pero la fecha
                // también puede tipearse a mano)
                if (validDepartureDate) {
                    var departureDay = new Date(this.departureDate);
                    departureDay.setHours(0, 0, 0, 0);
                    if (departureDay < this.departureDateMin) {
                        this.messageErrorDate = 'La fecha de salida debe ser a partir de mañana.';
                        return;
                    }
                }

                // Validar que la fecha de regreso sea posterior a la de salida
                if (validDepartureDate && validReturnDate) {
                    var departure = new Date(this.departureDate);
                    var returnD = new Date(this.returnDate);
                    if (returnD <= departure) {
                        this.messageErrorDate = 'La fecha de regreso debe ser posterior a la fecha de salida.';
                        return;
                    }

                    if (this.isSingleTrip && this.toStartOfDay(returnD) < this.getReturnDateMin(departure)) {
                        this.messageErrorDate = 'Para un viaje único, la fecha de regreso tiene que ser al menos 2 días después de la de salida.';
                        return;
                    }

                    if (this.isSingleTrip && this.serviceDateError) {
                        this.messageErrorDate = this.serviceDateError;
                        return;
                    }
                }

                if (this.totalPassengers < 1) {
                    this.messageErrorPassengers = 'Debés indicar al menos 1 pasajero.';
                    return;
                }

                if (this.totalPassengers > this.passengersTotalMax) {
                    this.messageErrorPassengers = `El total de pasajeros no puede superar ${this.passengersTotalMax}.`;
                    return;
                }

                if (validDepartureDate && validReturnDate && validInstallment) {
                    // Guardar datos del viaje en sessionStorage
                    const travelData = {
                        destination: this.selectedDestination,
                        tripType: this.selectedTripType,
                        departureDate: this.departureDate,
                        returnDate: this.returnDate,
                        installment: this.selectedInstallment,
                        passengersUnder21: this.passengersUnder21,
                        passengersBetween21And75: this.passengersBetween21And75,
                        passengersOver75: this.passengersOver75,
                        passengersCount: this.totalPassengers,
                    };
                    sessionStorage.setItem("TravelDetailsData", JSON.stringify(travelData));

                    // volver a planes desde el tomador o con el back del navegador no recotiza.
                    await this.quotePlans(travelData);
                }
            },

            async quotePlans(travelData: any) {
                // Nunca dejar una cotización vieja al lado de datos de viaje nuevos.
                sessionStorage.removeItem("TravelQuotationData");

                const dataTabHome = JSON.parse(sessionStorage.getItem("DataTabHome"));
                const body = {
                    departureDate: this.formatDateMMDDYYYY(travelData.departureDate),
                    returnDate: this.formatDateMMDDYYYY(travelData.returnDate),
                    destinationId: travelData.destination ? travelData.destination.id : 0,
                    quantityOfTravelId: travelData.tripType ? travelData.tripType.id : 0,
                    quotaQuantity: travelData.installment ? travelData.installment.id : 0,
                    quantityMinor: travelData.passengersUnder21 || 0,
                    quantityAdult: travelData.passengersBetween21And75 || 0,
                    quantityAdultGreater: travelData.passengersOver75 || 0,
                    cityId: dataTabHome && dataTabHome.selectedLocality ? parseInt(dataTabHome.selectedLocality.id) : 0,
                    zipCode: dataTabHome && dataTabHome.selectedLocality ? dataTabHome.selectedLocality.zipCode : 0,
                    provinceCode: dataTabHome && dataTabHome.selectedLocality ? (dataTabHome.selectedLocality.provinceId || 0) : 0,
                };

                this.startQuoteLoadingMessages();
                try {
                    const response = await travelAssistanceService.quotePlans(body);
                    sessionStorage.setItem("TravelQuotationData", JSON.stringify(response));
                    this.$router.push({ name: 'travelAssistance-tab-three' });
                } catch (error) {
                    const statusCode = error.response?.status;

                    if (statusCode === 400 && error.response.data?.message) {
                        this.messageErrorDate = error.response.data.message;
                        return;
                    }
                    this.Bus.$emit('show-service-fail', statusCode, [() => this.quotePlans(travelData)]);
                } finally {
                    this.stopQuoteLoadingMessages();
                    NF.UI.Page.unblock();
                }
            },

            async validateDatesEarly() {
                const seq = ++this.dateValidationSeq;
                this.serviceDateError = null;

                if (!this.isSingleTrip || this.departureDate == null || this.returnDate == null) return;
                if (this.toStartOfDay(this.returnDate) < this.getReturnDateMin(this.departureDate)) return;

                const departureDate = this.formatDateMMDDYYYY(this.departureDate);
                const returnDate = this.formatDateMMDDYYYY(this.returnDate);
                if (!departureDate || !returnDate) return;

                try {
                    const response = await travelAssistanceService.validateQuotationDate(departureDate, returnDate);
                    if (seq !== this.dateValidationSeq) return;
                    if (response && response.isValid === false) {
                        this.serviceDateError = response.message || 'Las fechas seleccionadas no son válidas para un viaje único.';
                        this.messageErrorDate = this.serviceDateError;
                    }
                } catch (error) {
                    // la validación que decide es la de QuotePlans
                }
            },

            startQuoteLoadingMessages() {
                const messages = [
                    { text: "Estamos buscando tu mejor cotización...", spin: '<i class="fa fa-asterisk fa-spin fa-3x"></i>' },
                    { text: "Comparando coberturas y precios…", spin: '<i class="fa fa-circle-o-notch fa-spin fa-3x"></i>' },
                    { text: "Casi listo. Estamos confirmando tu mejor cotización…", spin: '<i class="fa fa-asterisk fa-spin fa-3x"></i>' },
                ];
                NF.UI.Page.block({ message: messages[0].text, spin: messages[0].spin } as any);

                let current = 0;
                this.quoteLoadingTimer = setInterval(() => {
                    const span = document.querySelector("div.blockUI span");
                    if (!span) return;
                    current = (current + 1) % messages.length;
                    span.textContent = messages[current].text;
                    const icon = document.querySelector("div.blockUI i") || document.querySelector("div.blockUI svg");
                    if (icon) icon.outerHTML = messages[current].spin;
                }, 3000);
            },

            stopQuoteLoadingMessages() {
                if (this.quoteLoadingTimer) {
                    clearInterval(this.quoteLoadingTimer);
                    this.quoteLoadingTimer = null;
                }
            },

            formatDateMMDDYYYY(value: any): string {
                if (!value) return '';
                const d = value instanceof Date ? value : new Date(value);
                if (isNaN(d.getTime())) return '';
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            },

            toStartOfDay(value: any): Date {
                const d = new Date(value);
                d.setHours(0, 0, 0, 0);
                return d;
            },

            getReturnDateMin(departure: any): Date {
                const min = this.toStartOfDay(departure);
                min.setDate(min.getDate() + (this.isSingleTrip ? SINGLE_TRIP_MIN_RETURN_OFFSET_DAYS : 1));
                return min;
            },

            volver() {
                NF.UI.Page.block();
                sessionStorage.setItem("pressBack", "true");
                this.$router.push({
                    name: 'travelAssistance-trip-type',
                });
            },

            // Deriva fechas y cuota a partir del tipo de viaje seleccionado en la pantalla de cards.
            // Salida por defecto: mañana; regreso: salida +2 días (viaje único) o +1 año calendario (anual).
            applyTripTypeDefaults() {
                var departureRef = new Date();
                departureRef.setDate(departureRef.getDate() + 1);
                this.departureDate = departureRef;
                var returnRef = new Date(departureRef);
                if (this.isSingleTrip) {
                    returnRef = this.getReturnDateMin(departureRef);
                } else {
                    returnRef.setFullYear(returnRef.getFullYear() + 1);
                }
                this.returnDate = returnRef;
                this.returnDateKey = new Date().getTime();

                // Viaje unico -> 1 cuota autoseleccionada
                if (this.isSingleTrip) {
                    this.selectedInstallment = this.allInstallmentOptions.find((o: any) => o.id === 1) || null;
                }
            },

            getInitData() {
                sessionStorage.setItem("ExitOrContinueModal", "true");
                window.globalThis.dataLayer = window.globalThis.dataLayer || [];

                var referente = sessionStorage.getItem("Referente");
                referente == 'null' || referente == null ? this.referente = null : this.referente = referente;

                // Selecciones hechas en las pantallas de cards previas
                var destinationRaw = JSON.parse(sessionStorage.getItem("TravelDestinationSelected"));
                var tripTypeRaw = JSON.parse(sessionStorage.getItem("TravelTripTypeSelected"));
                if (destinationRaw == null || tripTypeRaw == null) {
                    // Guard: entrada directa por URL sin selección previa
                    this.$router.push({ name: 'travelAssistance-destination' });
                    return;
                }
                this.selectedDestination = { id: destinationRaw.code, description: destinationRaw.productDescription };
                this.selectedTripType = { id: tripTypeRaw.code, description: tripTypeRaw.productDescription };

                var validBack = sessionStorage.getItem("pressBackFromPlans");
                var travelDataJson = null;
                if (validBack) {
                    sessionStorage.removeItem("pressBackFromPlans");
                    var travelData = sessionStorage.getItem("TravelDetailsData");
                    if (travelData != null) {
                        travelDataJson = JSON.parse(travelData);
                    }
                }

                if (travelDataJson != null && String(travelDataJson.tripType?.id) === String(this.selectedTripType.id)) {
                    // Vuelve de planes sin cambiar el tipo de viaje: restaurar todo tal como estaba
                    this.isRestoringData = true;
                    this.departureDate = travelDataJson.departureDate;
                    this.returnDate = travelDataJson.returnDate;
                    this.returnDateMin = this.getReturnDateMin(travelDataJson.departureDate);
                    this.selectedInstallment = travelDataJson.installment ?? null;
                    this.passengersUnder21 = travelDataJson.passengersUnder21 ?? 0;
                    this.passengersBetween21And75 = travelDataJson.passengersBetween21And75 ?? 0;
                    this.passengersOver75 = travelDataJson.passengersOver75 ?? 0;
                    this.returnDateKey = new Date().getTime();
                    // El flush de watchers de Vue corre antes que este callback
                    this.$nextTick(() => { this.isRestoringData = false; });
                } else {
                    // Primera entrada, o volvió atrás y cambió el tipo de viaje: re-derivar fechas/cuota
                    this.applyTripTypeDefaults();
                    if (travelDataJson != null) {
                        // Los pasajeros no dependen del tipo de viaje, se conservan
                        this.passengersUnder21 = travelDataJson.passengersUnder21 ?? 0;
                        this.passengersBetween21And75 = travelDataJson.passengersBetween21And75 ?? 0;
                        this.passengersOver75 = travelDataJson.passengersOver75 ?? 0;
                    }
                }

                var dataTabHome = JSON.parse(sessionStorage.getItem("DataTabHome"));
                window.globalThis.dataLayer.push({
                    'pas': null,
                    'email': dataTabHome ? dataTabHome.email : null,
                    'referente': this.referente,
                    'product': 'asistencia-viaje',
                    'event': 'cotizador-asistencia-viaje-datos-viaje'
                });
                window.globalThis._qsie = window.globalThis._qsie || [];
                window.globalThis._qsie.push('cotizador-asistencia-viaje-datos-viaje');
            },
        },
        watch: {
            passengersUnder21(newVal) {
                this.clampPassengerGroup('passengersUnder21', newVal);
                if (this.totalPassengers >= 1) this.messageErrorPassengers = null;
            },
            passengersBetween21And75(newVal) {
                this.clampPassengerGroup('passengersBetween21And75', newVal);
                if (this.totalPassengers >= 1) this.messageErrorPassengers = null;
            },
            passengersOver75(newVal) {
                this.clampPassengerGroup('passengersOver75', newVal);
                if (this.totalPassengers >= 1) this.messageErrorPassengers = null;
            },
            departureDate(newVal) {
                if (newVal == null) return;
                // No recalcular mientras se restauran los datos guardados (el flush del
                // watcher es asíncrono y pisaría la fecha de regreso restaurada)
                if (this.isRestoringData) return;

                this.messageErrorDate = null;

                // El mínimo de regreso es salida + 2 en viaje unico
                const minReturn = this.getReturnDateMin(newVal);
                this.returnDateMin = minReturn;

                // Recalcular returnDate según el tipo de viaje (o ajustar si quedó inválida)
                if (this.isSingleTrip) {
                    const maxReturn = this.toStartOfDay(newVal);
                    maxReturn.setFullYear(maxReturn.getFullYear() + 1);
                    const currentReturn = this.returnDate != null ? this.toStartOfDay(this.returnDate) : null;
                    if (currentReturn == null || isNaN(currentReturn.getTime()) || currentReturn < minReturn || currentReturn > maxReturn) {
                        this.returnDate = minReturn;
                    } else {
                        this.validateDatesEarly();
                    }
                } else if (this.selectedTripType) {
                    const oneYearLater = new Date(newVal);
                    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
                    this.returnDate = oneYearLater;
                } else if (this.returnDate != null && new Date(this.returnDate) <= new Date(newVal)) {
                    // Sin tipo de viaje seleccionado aún, solo ajustamos si quedó inválida
                    this.returnDate = minReturn;
                }

                this.returnDateKey = new Date().getTime();
            },
            returnDate() {
                if (this.isRestoringData) return;
                if (this.serviceDateError && this.messageErrorDate === this.serviceDateError) {
                    this.messageErrorDate = null;
                }
                this.validateDatesEarly();
            },
        },


        async mounted() {
            try {
                NF.UI.Page.block();
                await this.pageCustomization();
                await this.updateByProductGoogleAnalytics();
                await this.getInitData();
            } finally {
                NF.UI.Page.unblock();
            }
        },
    })
